// src/components/gtm.tsx
import { useEffect } from "react";
import { parseCookies } from "@wbly/common";

// src/js/init-gtm.js
var initGtm = (host, containerId, query) => {
  (function(w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": (/* @__PURE__ */ new Date()).getTime(), event: "gtm.js" });
    var f = d.getElementsByTagName(s)[0], j = d.createElement(s), dl = l != "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = `https://${host}/gtm.js?id=` + i + dl + (query ? `&${query}` : "");
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", `${containerId}`);
};

// src/events/collector.ts
var MissingRequiredDataError = class extends Error {
  constructor(missingField) {
    super(`Failed to construct event, missing required data: ${missingField}`);
    this.missingField = missingField;
  }
};
var MissingImportantDataError = class extends Error {
  constructor(missingFields) {
    super(`Missing important attributes: [${missingFields.join(", ")}]`);
    this.missingFields = missingFields;
  }
};
var CollectionFactory = class {
  constructor(ecommerceData) {
    this.ecommerceData = ecommerceData;
  }
  missingImportantAttributes = [];
  getMissingImportantAttributes() {
    return this.missingImportantAttributes;
  }
  optional() {
    return new FieldAtlas(this.ecommerceData, (_, value) => value);
  }
  required() {
    return new FieldAtlas(this.ecommerceData, (fieldName, value) => {
      if (value === void 0) {
        throw new MissingRequiredDataError(fieldName);
      }
      return value;
    });
  }
  important() {
    return new FieldAtlas(this.ecommerceData, (fieldName, value) => {
      if (value === void 0) {
        this.missingImportantAttributes.push(fieldName);
      }
      return value;
    });
  }
};
var FieldAtlas = class {
  constructor(ecommerceData, collect) {
    this.ecommerceData = ecommerceData;
    this.collect = collect;
  }
  // Currency and value collectors
  currency() {
    return this.collect("currency", this.ecommerceData.marketplace.currency);
  }
  value() {
    return this.collect("value", this.ecommerceData.order?.total);
  }
  // Transaction collectors
  transactionId() {
    return this.collect(
      "transaction_id",
      this.ecommerceData.orderIdentifier ?? this.ecommerceData.order?.number
    );
  }
  coupon() {
    return this.collect("coupon", this.ecommerceData.order?.coupon);
  }
  shipping() {
    return this.collect("shipping", this.ecommerceData.order?.shipping?.price);
  }
  tax() {
    return this.collect("tax", this.ecommerceData.order?.tax);
  }
  // Item collectors
  items() {
    return this.collect("items", this.ecommerceData.order?.items);
  }
  itemId(index) {
    return this.collect(
      `items[${index}].item_id`,
      this.ecommerceData.order?.items[index]?.sku
    );
  }
  itemName(index) {
    return this.collect(
      `items[${index}].item_name`,
      this.ecommerceData.order?.items[index]?.name
    );
  }
  itemBrand(index) {
    return this.collect(
      `items[${index}].item_brand`,
      this.ecommerceData.order?.items[index]?.brand
    );
  }
  itemCategory(index) {
    return this.collect(
      `items[${index}].item_category`,
      this.ecommerceData.order?.items[index]?.category
    );
  }
  itemVariant(index) {
    return this.collect(
      `items[${index}].item_variant`,
      this.ecommerceData.order?.items[index]?.variant
    );
  }
  itemPrice(index) {
    return this.collect(
      `items[${index}].item_price`,
      this.ecommerceData.order?.items[index]?.price
    );
  }
  itemQuantity(index) {
    return this.collect(
      `items[${index}].item_quantity`,
      this.ecommerceData.order?.items[index]?.quantity
    );
  }
  itemDiscount(index) {
    return this.collect(
      `items[${index}].item_discount`,
      this.ecommerceData.order?.items[index]?.productCoupon ? this.ecommerceData.order.items[index].price * this.ecommerceData.order.items[index].productCoupon / 100 : void 0
    );
  }
  itemCoupon(index) {
    return this.collect(
      `items[${index}].item_coupon`,
      this.ecommerceData.order?.items[index]?.productCoupon ? `${this.ecommerceData.order.items[index].productCoupon}%` : void 0
    );
  }
  price(index) {
    return this.collect(
      `items[${index}].price`,
      this.ecommerceData.order?.items[index]?.price
    );
  }
  quantity(index) {
    return this.collect(
      `items[${index}].quantity`,
      this.ecommerceData.order?.items[index]?.quantity
    );
  }
  discount(index) {
    return this.collect(
      `items[${index}].discount`,
      this.ecommerceData.order?.items[index]?.productCoupon
    );
  }
  // User property collectors
  userEmailSha256() {
    return this.collect("user.email", this.ecommerceData.user?.email);
  }
  userType() {
    return this.collect(
      "user.authenticated",
      this.ecommerceData.user?.authenticated
    );
  }
  userRegion() {
    return this.collect(
      "user_region",
      this.ecommerceData.user?.address?.region
    );
  }
  userCity() {
    return this.collect("user_city", this.ecommerceData.user?.address?.city);
  }
  userCountry() {
    return this.collect(
      "user_country",
      this.ecommerceData.user?.address?.country
    );
  }
  // Marketplace collectors
  marketplaceLanguage() {
    return this.collect(
      "marketplace.language",
      this.ecommerceData.marketplace.language
    );
  }
  marketplaceCountry() {
    return this.collect(
      "marketplace.country",
      this.ecommerceData.marketplace.country
    );
  }
  // Order collectors
  orderNumber() {
    return this.collect(
      "order.number",
      this.ecommerceData.orderIdentifier ?? this.ecommerceData.order?.number
    );
  }
  orderTotal() {
    return this.collect("order.total", this.ecommerceData.order?.total);
  }
  orderSubTotal() {
    return this.collect("order.subTotal", this.ecommerceData.order?.subTotal);
  }
  orderDiscount() {
    return this.collect("order.discount", this.ecommerceData.order?.discount);
  }
  orderTax() {
    return this.collect("order.tax", this.ecommerceData.order?.tax);
  }
  // Shipping collectors
  shippingMethod() {
    return this.collect(
      "order.shipping.method",
      this.ecommerceData.order?.shipping?.method
    );
  }
  shippingType() {
    return this.collect(
      "order.shipping.type",
      this.ecommerceData.order?.shipping?.type
    );
  }
  shippingDiscount() {
    return this.collect(
      "order.shipping.discount",
      this.ecommerceData.order?.shipping?.discount
    );
  }
  // User collectors
  userPhoneSha256() {
    return this.collect("user.phone", this.ecommerceData.user?.phone);
  }
  userFirstnameSha256() {
    return this.collect("user.firstname", this.ecommerceData.user?.firstname);
  }
  userLastnameSha256() {
    return this.collect("user.lastname", this.ecommerceData.user?.lastname);
  }
  // User address collectors
  userAddressStreet() {
    return this.collect(
      "user.address.street",
      this.ecommerceData.user?.address?.street
    );
  }
  userAddressCity() {
    return this.collect(
      "user.address.city",
      this.ecommerceData.user?.address?.city
    );
  }
  userAddressRegion() {
    return this.collect(
      "user.address.region",
      this.ecommerceData.user?.address?.region
    );
  }
  userAddressCountry() {
    return this.collect(
      "user.address.country",
      this.ecommerceData.user?.address?.country
    );
  }
  userAddressPostalCode() {
    return this.collect(
      "user.address.postal_code",
      this.ecommerceData.user?.address?.postal_code
    );
  }
};

// src/events/purchase.ts
var purchase = (ecommerce) => {
  const collect = new CollectionFactory(ecommerce);
  const purchaseEvent = {
    event: "purchase",
    ecommerce: {
      transaction_id: collect.required().transactionId(),
      currency: collect.important().currency(),
      value: collect.important().value(),
      coupon: collect.optional().coupon(),
      shipping: collect.optional().shipping(),
      tax: collect.optional().tax(),
      items: collect.important().items()?.map((_, index) => ({
        item_id: collect.important().itemId(index),
        item_name: collect.optional().itemName(index),
        item_brand: collect.optional().itemBrand(index),
        item_category: collect.optional().itemCategory(index),
        item_variant: collect.optional().itemVariant(index),
        price: collect.optional().itemPrice(index),
        quantity: collect.optional().itemQuantity(index),
        coupon: collect.optional().itemCoupon(index),
        discount: collect.optional().itemDiscount(index),
        index
      })) ?? []
    },
    user_properties: {
      sha256_email: collect.important().userEmailSha256(),
      sha256_phone_number: collect.optional().userPhoneSha256(),
      authenticated: collect.optional().userType(),
      address: {
        sha256_first_name: collect.optional().userFirstnameSha256(),
        sha256_last_name: collect.optional().userLastnameSha256(),
        street: collect.optional().userAddressStreet(),
        city: collect.optional().userAddressCity(),
        region: collect.optional().userAddressRegion(),
        postal_code: collect.optional().userAddressPostalCode(),
        country: collect.optional().userAddressCountry()
      }
    }
  };
  return {
    builtEvent: purchaseEvent,
    missingData: collect.getMissingImportantAttributes()
  };
};

// src/events/index.ts
var UnrecognisedEventError = class extends Error {
  constructor(eventName) {
    super(`Unrecognised event: ${eventName}`);
  }
};
var EventFactory = class {
  build(ecommerceData, queuedEvent) {
    switch (queuedEvent.name) {
      case "purchase":
        return purchase(ecommerceData);
      default:
        throw new UnrecognisedEventError(queuedEvent.name);
    }
  }
};

// src/event-pipeline.ts
var consentUpdateEvent = "cookie_consent_update";
var EventPipeline = class {
  constructor(window2, eventFactory, reportError) {
    this.window = window2;
    this.eventFactory = eventFactory;
    this.reportError = reportError;
    this.window.addEventListener(datalayerPushEvent, (event) => {
      if (this.isDataLayerPushJsEvent(event) && event.detail.event === consentUpdateEvent) {
        this.confirmConsentSet();
      }
    });
  }
  // been around the block on this one, but it's my understanding that the client
  // dataLayer model is not reliable when considering pushed events due to the proxy
  // constructed around it via GTM. Therefore, to look for a source of truth as to
  // which custom events have been fired, we can inspect the pipeline instead
  pushedEvents = [];
  isConsentSet = false;
  ecommerceModelStatus = {
    status: "pending"
  };
  eventsToBuild = [];
  eventsToPush = [];
  successfullyLoadedEcommerceModel(ecommerceModel) {
    this.ecommerceModelStatus = {
      status: "ready",
      ecommerceModel
    };
    this.buildEventsFromQueue(this.ecommerceModelStatus.ecommerceModel);
    if (this.isConsentSet) {
      this.pushBuiltEventsToDataLayer();
    }
  }
  failedToLoadEcommerceModel(error) {
    this.ecommerceModelStatus = {
      status: "error",
      error
    };
    this.handleLoadEcommerceDataModelError(error);
  }
  confirmConsentSet() {
    this.isConsentSet = true;
    if (this.ecommerceModelStatus.status === "ready") {
      this.pushBuiltEventsToDataLayer();
    } else if (this.ecommerceModelStatus.status === "error") {
      this.handleLoadEcommerceDataModelError(this.ecommerceModelStatus.error);
    }
  }
  pushEvent(event) {
    this.eventsToBuild.push(event);
    if (this.ecommerceModelStatus.status === "ready") {
      this.buildEventsFromQueue(this.ecommerceModelStatus.ecommerceModel);
      if (this.isConsentSet) {
        this.pushBuiltEventsToDataLayer();
      }
    } else if (this.ecommerceModelStatus.status === "error") {
      this.handleLoadEcommerceDataModelError(this.ecommerceModelStatus.error);
    }
  }
  // we want to check if any events that are to be pushed are critical, if so we want to report
  // the error to provoke investigation
  handleLoadEcommerceDataModelError(error) {
    for (const event of this.eventsToBuild) {
      if (event.urgency === "critical") {
        this.reportError({
          name: "ecommerce-load-error",
          message: `Failed to load ecommerce model for critical event '${event.name}'`,
          context: {
            event: event.name,
            error
          }
        });
      }
    }
    this.eventsToBuild = [];
  }
  buildEventsFromQueue(ecommerceModel) {
    for (const event of this.eventsToBuild) {
      try {
        const { builtEvent, missingData } = this.eventFactory.build(
          ecommerceModel,
          event
        );
        if (missingData.length > 0) {
          this.reportError({
            name: "missing-important-event-data",
            message: `Missing important data when building event, event: '${event.name}'`,
            context: {
              event: event.name,
              error: new MissingImportantDataError(missingData),
              ecommerceModel
            }
          });
        }
        this.eventsToPush.push(builtEvent);
      } catch (err) {
        if (err instanceof UnrecognisedEventError) {
          this.reportError({
            name: "unrecognised-event",
            message: `Unrecognised event: '${event.name}'`,
            context: { event: event.name, error: err }
          });
          continue;
        }
        if (err instanceof MissingRequiredDataError) {
          this.reportError({
            name: "failed-to-build-event",
            message: `Failed to build event due to missing required data, event: '${event.name}'`,
            context: { event: event.name, error: err, ecommerceModel }
          });
          continue;
        }
        this.reportError({
          name: "failed-to-build-event",
          message: `Failed to build event due to unexpected error, event: '${event.name}'`,
          context: { event: event.name, error: err, ecommerceModel }
        });
      }
    }
    this.eventsToBuild = [];
  }
  pushBuiltEventsToDataLayer() {
    for (const event of this.eventsToPush) {
      this.window.dataLayer.push(event);
      this.pushedEvents.push(event);
    }
    this.eventsToPush = [];
  }
  isDataLayerPushJsEvent(event) {
    if (!event || typeof event !== "object")
      return false;
    const { detail } = event;
    if (!detail || typeof detail !== "object")
      return false;
    return "event" in detail && typeof detail.event === "string";
  }
};

// src/parse-response.ts
var ResponseError = class extends Error {
  constructor(status, statusText, body) {
    super(`${status} ${statusText}`);
    this.status = status;
    this.statusText = statusText;
    this.body = body;
    this.name = "ResponseError";
  }
};
var parseResponse = async (response) => {
  if (!response.ok) {
    let body;
    if (response.headers.get("Content-Type") === "application/json") {
      body = await response.json();
    } else {
      body = await response.text();
    }
    throw new ResponseError(response.status, response.statusText, body);
  }
  return await response.json();
};

// src/get-ecommerce-model.ts
var getEcommerceModel = async (endpoint, orderNumber) => {
  let url = endpoint;
  if (orderNumber) {
    url = `${url}?order_number=${orderNumber}`;
  }
  const response = await fetch(url);
  return await parseResponse(response);
};

// src/report-error.ts
var reportEventError = async (endpoint, context) => {
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(context)
    });
    return await parseResponse(response);
  } catch (err) {
    if (err instanceof ResponseError) {
      console.error(
        "Failed to report event error",
        err.status,
        err.statusText,
        err.body
      );
    } else {
      console.error("Failed to report event error", err, context);
    }
  }
};

// src/components/gtm.tsx
var gtmDisabledCookieName = "disable_gtm";
var datalayerPushEvent = "datalayer-push";
var initDataLayer = (initialDataLayer) => {
  window.dataLayer = new Proxy(window.dataLayer || initialDataLayer || [], {
    set: (target, prop, value) => {
      if (prop !== "length") {
        const pushEvent2 = new CustomEvent(datalayerPushEvent, {
          detail: value
        });
        window.dispatchEvent(pushEvent2);
      }
      return Reflect.set(target, prop, value);
    }
  });
};
var GTM = ({
  initialDataLayer,
  host,
  containerId,
  query,
  ecommerceEndpoint,
  reportErrorEndpoint,
  orderNumber,
  enabledFeatures,
  pageActionEvent
}) => {
  useEffect(() => {
    if (parseCookies(document.cookie)[gtmDisabledCookieName]) {
      return;
    }
    initDataLayer(initialDataLayer);
    initGtm(host, containerId, query);
    const eventFactory = new EventFactory();
    window.eventPipeline = new EventPipeline(
      window,
      eventFactory,
      (ctx) => reportEventError(reportErrorEndpoint, ctx)
    );
    getEcommerceModel(ecommerceEndpoint, orderNumber).then((ecommerceModel) => {
      window.eventPipeline.successfullyLoadedEcommerceModel(ecommerceModel);
    }).catch((err) => {
      window.eventPipeline.failedToLoadEcommerceModel(err);
    });
    if (pageActionEvent) {
      pushEvent(pageActionEvent);
    }
    if (enabledFeatures) {
      enabledFeatures.forEach((feature) => {
        window.dataLayer.push({
          event: "optimizely-decision-fs",
          "optimizely-flagKey": feature.featureKey,
          "optimizely-ruleKey": feature.ruleKey,
          "optimizely-variationKey": feature.variationKey
        });
      });
    }
  }, []);
  return null;
};

// src/components/gtm-no-script.tsx
import { jsx } from "react/jsx-runtime";
var GTMNoScript = ({ host, containerId, query }) => /* @__PURE__ */ jsx("noscript", { children: /* @__PURE__ */ jsx(
  "iframe",
  {
    src: `https://${host}/ns.html?id=${containerId}&${query}`,
    height: "0",
    width: "0",
    style: {
      display: "none",
      visibility: "hidden"
    }
  }
) });

// src/index.ts
var pushEvent = (event) => {
  window.eventPipeline.pushEvent(event);
};
export {
  GTM,
  GTMNoScript,
  pushEvent
};
